import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../hooks/useNotifications';
import type { NotificationItem } from '../../services/api';
import { Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SPRING_FAST } from '../ui/motion';
import { destinationFor } from '../../lib/notificationRouting';
import './NotificationBell.css';

interface NotificationBellProps {
  /** Disables polling + rendering when no user is logged in. */
  enabled?: boolean;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ enabled = true }) => {
  const { notifications, unreadCount, loading, markRead, markAllRead, refetch } =
    useNotifications(enabled);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleOpen = useCallback(() => {
    setOpen((v) => {
      const next = !v;
      if (next) {
        // Eagerly mark everything visible as read.
        if (unreadCount > 0) markAllRead();
        // Light refetch in case poll is stale.
        refetch();
      }
      return next;
    });
  }, [unreadCount, markAllRead, refetch]);

  const handleClickItem = useCallback(
    (n: NotificationItem) => {
      markRead(n);
      const dest = destinationFor(n);
      if (dest) {
        setOpen(false);
        navigate(dest);
      }
    },
    [markRead, navigate],
  );

  if (!enabled) return null;

  return (
    <div className="notif-bell-wrap" ref={wrapRef}>
      <button
        type="button"
        className="notif-bell-trigger"
        data-tooltip={
          unreadCount > 0
            ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`
            : 'Notifications'
        }
        aria-label="Notifications"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={handleOpen}
      >
        <span className="notif-bell-icon" aria-hidden="true">
          <Bell size={18} strokeWidth={1.75} className="lnr-icon" />
        </span>
        {unreadCount > 0 ? (
          <span className="notif-bell-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        ) : null}
      </button>

      <AnimatePresence>
        {open ? (
          <motion.div
            className="notif-bell-dropdown"
            role="dialog"
            aria-label="Notifications"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, transition: { duration: 0.12 } }}
            transition={SPRING_FAST}
          >
            <div className="notif-bell-dropdown-head">
              <span className="notif-bell-dropdown-title">Notifications</span>
              {notifications.length > 0 ? (
                <span className="notif-bell-dropdown-meta">{notifications.length} recent</span>
              ) : null}
            </div>
            {loading && notifications.length === 0 ? (
              <div className="notif-bell-empty">Loading…</div>
            ) : notifications.length === 0 ? (
              <div className="notif-bell-empty">You're all caught up.</div>
            ) : (
              <ul className="notif-bell-list">
                {notifications.map((n) => (
                  <li
                    key={n.notifId}
                    className={`notif-bell-item ${n.readAt ? 'notif-bell-item--read' : 'notif-bell-item--unread'}`}
                  >
                    <button
                      type="button"
                      className="notif-bell-item-btn"
                      onClick={() => handleClickItem(n)}
                    >
                      <span className="notif-bell-item-icon" aria-hidden="true">
                        {iconFor(n)}
                      </span>
                      <span className="notif-bell-item-body">
                        <span className="notif-bell-item-text">{textFor(n)}</span>
                        <span className="notif-bell-item-meta">
                          {formatRelativeTime(n.createdAt)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

// ─── Type-specific renderers ──────────────────────────────────────────────
// Adding a new notification type? Add an icon + text mapping here. Unknown
// types fall back to the message string + a generic bell icon.

function iconFor(n: NotificationItem): string {
  switch (n.type) {
    case 'assignment_created':
      return '📋';
    case 'scenario_force_deleted':
      return '🚫';
    case 'scenario_reshared':
      return '📤';
    case 'scenario_promoted':
      return '⬆️';
    case 'submission_changes_requested':
      return '✏️';
    case 'submission_declined':
      return '🚫';
    // The four recurring team notifications. They arrive in the same list and
    // say very different things, and three of the four were reading as the
    // same generic bell.
    case 'team_weekly_summary':
      return '📊';
    case 'team_attention_alert':
      return '⚠️';
    case 'team_improvement_highlight':
      return '📈';
    case 'assignment_reminder':
      return '⏰';
    // Account deletion, from routes/users.js. Two sides of the same decision,
    // so one icon each rather than the generic bell for both.
    case 'account_deletion_requested':
      return '🗑️';
    case 'account_deletion_declined':
      return '↩️';
    default:
      return '🔔';
  }
}

function textFor(n: NotificationItem): string {
  if (n.message) return n.message;
  const p = (n.payload || {}) as Record<string, any>;
  switch (n.type) {
    case 'assignment_created':
      return p.assignedByName
        ? `${p.assignedByName} assigned you "${p.simulationTitle || 'a scenario'}"`
        : `New practice assignment: "${p.simulationTitle || 'a scenario'}"`;
    case 'scenario_force_deleted':
      return p.deletedByName
        ? `${p.deletedByName} removed "${p.simulationTitle || 'your scenario'}"${p.deleteReason ? ` — ${p.deleteReason}` : ''}`
        : `Your scenario was removed${p.deleteReason ? ` — ${p.deleteReason}` : ''}`;
    case 'scenario_reshared':
      return 'Your template was published to a library.';
    case 'scenario_promoted':
      return 'Your template was published to the company library.';
    // Both routes always send a `message` (built server-side with the title
    // and the reviewer's note), so these are last-resort fallbacks only.
    case 'submission_changes_requested':
      return 'A reviewer asked for changes on your template.';
    case 'submission_declined':
      return 'Your template was not added to the library.';
    default:
      return 'New notification';
  }
}

function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diffMs = Date.now() - t;
  const min = 60_000;
  const hr = 60 * min;
  const day = 24 * hr;
  if (diffMs < min) return 'just now';
  if (diffMs < hr) return `${Math.floor(diffMs / min)}m ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hr)}h ago`;
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default NotificationBell;
